import { FC, useEffect, useState } from 'react';
import pharmacySearch, {
  PharmacySearchResponse,
} from 'api/pharmacy/SearchPharmacy';
import { TreatPharmacy } from 'api/pharmacy/TreatPharmacy.types';
import { RegisterFn } from 'components/core/form';
import { Text } from 'components/core/text';
import { Select } from 'components/form/Select';
import { usePrevious } from 'hooks/usePrevious';
import { Props, SingleValue } from 'react-select';

const RadiusOptions = [
  { value: 5, label: '5 miles' },
  { value: 10, label: '10 miles' },
  { value: 15, label: '15 miles' },
  { value: 25, label: '25 miles' },
  { value: 50, label: '50 miles' },
];

interface OptionType {
  label: string;
  value: number;
  name: string;
  address: string;
}

export interface PharmacySearchProps {
  zip?: string;
  onChange: (pharmacy: TreatPharmacy | null) => void;
  registerField: RegisterFn;
  pharmacyId?: string;
}

const PharmacySearch: FC<PharmacySearchProps> = ({
  zip: zipProp,
  onChange,
  registerField,
  pharmacyId,
}) => {
  const [query, setQuery] = useState<OptionType | null>(null);
  const [zip, setZip] = useState('');
  const [radius, setRadius] = useState(RadiusOptions[1].value);
  const [data, setData] = useState<PharmacySearchResponse>();

  const [formattedOptions, setFormattedOptions] = useState<Props['options']>(
    [],
  );

  const prevZipProp = usePrevious(zipProp);
  const prevZip = usePrevious(zip);

  const prevRadius = usePrevious(radius);

  const setRadiusOption = (o: SingleValue<{ label: string; value: number }>) =>
    setRadius(o ? Number(o.value) : 10);

  useEffect(() => {
    const fetchData = async () => {
      const data = await pharmacySearch({
        zip,
        radius,
      });

      setData(data);

      const formattedOptions =
        data?.pharmacies?.map(option => ({
          value: option.id,
          label: (
            <div className="flex flex-col">
              <Text variant="heading6">{option.pharmStoreName}</Text>
              <Text>{option.pharmAddressLine1}</Text>
            </div>
          ),
          name: option.pharmStoreName,
          address: option.pharmAddressLine1,
        })) ?? [];

      setFormattedOptions(formattedOptions);
      if (!query && pharmacyId) {
        const foundPharmacy = formattedOptions.find(
          o => o.value.toString() === pharmacyId,
        );

        if (foundPharmacy) {
          setQuery({
            ...foundPharmacy,
            label: `${foundPharmacy.name}, ${foundPharmacy.address}`,
          });
        }
      }
    };

    if (zip && zip.length === 5 && (zip !== prevZip || prevRadius !== radius))
      fetchData();
  }, [formattedOptions, radius, zip, prevZip, prevRadius, query, pharmacyId]);

  // When the consuming component first sets a 5-digit zipcode, set
  // the zip here for convenience.
  useEffect(() => {
    if (zipProp && zipProp.length === 5 && zipProp !== prevZipProp) {
      setZip(zipProp);
    }
  }, [prevZipProp, zipProp]);

  const handlePharmacySearch = (pharmacy: SingleValue<OptionType>) => {
    if (pharmacy) {
      setQuery(pharmacy);

      if (data?.pharmacies.length !== 0) {
        const pharmacyData = data?.pharmacies.filter(item => {
          return item.id === pharmacy.value;
        });
        if (pharmacyData) onChange(pharmacyData[0]);
      }
    }
  };

  return (
    <>
      <div className="flex flex-col">
        <div className="flex flex-row w-full justify-between items-center">
          <div className="flex flex-col w-full">
            <Select
              label="Miles from Zip"
              onChange={setRadiusOption}
              options={RadiusOptions}
              defaultValue={RadiusOptions[1]}
            />
          </div>
        </div>
      </div>
      <Select
        label="Select your pharmacy"
        id="doctors"
        options={formattedOptions}
        placeholder="Select"
        onChange={(e: unknown) => {
          const option = e as OptionType;
          handlePharmacySearch({
            ...option,
            label: `${option.name}, ${option.address}`,
          });
        }}
        {...registerField('ayvaPharmacyUid', {
          validators: [],
        })}
        minMenuHeight={250}
        isDisabled={!zip || !radius}
        value={query}
      />
    </>
  );
};

export default PharmacySearch;
